import { createSlice, PayloadAction, PrepareAction } from '@reduxjs/toolkit'

interface Release {
  releaseid: string | number
  flaggedtrackcount?: number
  [key: string]: unknown
}

interface FlaggedState {
  page: number
  totalItemsCount: number | null
  sort: string | null
  loading: boolean
  releases: Release[]
  error?: unknown
}

const initialState: FlaggedState = {
  page: 0,
  totalItemsCount: null,
  sort: null,
  loading: false,
  releases: [],
}

const flaggedSlice = createSlice({
  name: 'flagged',
  initialState,
  reducers: {
    clearAllStart: (state) => {
      state.loading = true
    },
    clearAllSuccess: () => initialState,
    clearAllError: (state) => {
      state.loading = false
    },
    removeReleaseStart: (state) => {
      state.loading = true
    },
    removeReleaseSuccess: (state) => {
      state.loading = false
    },
    removeReleaseError: (state) => {
      state.loading = false
    },
    sortFlaggedBy: (state, action: PayloadAction<string>) => {
      state.sort = action.payload
    },
    removeFlaggedRelease: (state, action: PayloadAction<Release>) => {
      state.releases = state.releases.filter(
        (item) => item.releaseid !== action.payload.releaseid
      )
    },
    removeFlaggedReleaseId: (state, action: PayloadAction<string | number>) => {
      state.releases = state.releases.filter(
        (item) => item.releaseid !== action.payload
      )
    },
    addFlaggedRelease: (state, action: PayloadAction<Release>) => {
      const exists = state.releases.some(
        (obj) => obj.releaseid === action.payload.releaseid
      )
      if (!exists) {
        state.releases.unshift(action.payload)
      }
    },
    updateFlaggedTrackCountForRelease: {
      prepare(releaseid: string | number, flaggedtrackcount: number) {
        return { payload: { releaseid, flaggedtrackcount } }
      },
      reducer(state, action: PayloadAction<{ releaseid: string | number; flaggedtrackcount: number }>) {
        const { releaseid, flaggedtrackcount } = action.payload
        state.releases = state.releases.map((release) => ({
          ...release,
          flaggedtrackcount:
            release.releaseid === releaseid ? flaggedtrackcount : release.flaggedtrackcount,
        }))
      },
    },
    loadingFlagged: (state) => {
      state.loading = true
    },
    loadingFlaggedSuccess: {
      prepare(releases: Release[], page: number, totalItemsCount: number) {
        return { payload: { releases, page, totalItemsCount } }
      },
      reducer(state, action: PayloadAction<{ releases: Release[]; page: number; totalItemsCount: number }>) {
        const { releases, page, totalItemsCount } = action.payload
        const newReleases = (page === 1 ? [] : state.releases).concat(releases)
        if (page === state.page + 1) {
          state.releases = newReleases
          state.page = page
          state.totalItemsCount = totalItemsCount
          state.loading = false
        }
      },
    },
    loadingFlaggedFail: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload
      state.loading = false
    },
    refreshFlagged: (state) => {
      state.page = 0
      state.totalItemsCount = null
      state.releases = []
    },
    reset: () => initialState,
  },
})

export const flaggedActions = flaggedSlice.actions
export const flaggedReducer = flaggedSlice.reducer
export default flaggedActions
